import React, { useEffect, useState } from "react";
import {
  Store, Loader2, Gift, Plus, Trash2, X, BarChart3, Printer, ShieldCheck,
  Trophy, Users2, Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * SponsorBooths — booth list, spin wheel giveaway, and organiser tools
 * (create booth, download A4/A3 QR poster, view booth analytics).
 */
export function SponsorBooths({ eventId, eventSlug, isOwner, autoSpinBoothId }) {
  const [booths, setBooths]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [showCreate, setShowCreate]   = useState(false);
  const [spinBooth, setSpinBooth]     = useState(null);
  const [analytics, setAnalytics]     = useState(null);   // {booth}

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/nfc/events/${eventId}/booths`);
      setBooths(r.data || []);
      // Deep-link support: ?spin=<booth_id> from QR poster
      if (autoSpinBoothId) {
        const b = (r.data || []).find(x => x.booth_id === autoSpinBoothId);
        if (b) setSpinBooth(b);
      }
    } catch { setBooths([]); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [eventId]);

  const removeBooth = async (b) => {
    if (!window.confirm(`Remove booth "${b.name}"?`)) return;
    try {
      await api.delete(`/nfc/events/${eventId}/booths/${b.booth_id}`);
      toast.success("Booth removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const toggleVerified = async (b) => {
    try {
      const r = await api.post(`/nfc/events/${eventId}/booths/${b.booth_id}/verify`, { verified: !b.verified });
      toast.success(r.data.verified ? "Booth verified ✓" : "Verification removed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  const posterUrl = (bid, size = "A4") =>
    `${process.env.REACT_APP_BACKEND_URL || ""}/api/nfc/events/${eventId}/booths/${bid}/poster.pdf?size=${size}`;

  return (
    <section data-testid="event-booths" className="rounded-3xl bg-white border border-stone-200 overflow-hidden">
      <div className="px-4 py-2 bg-gradient-to-r from-fuchsia-50 to-purple-50 border-b border-stone-200 flex items-center gap-2 flex-wrap">
        <Store size={13} className="text-fuchsia-700"/>
        <span className="text-[11px] uppercase tracking-widest font-black text-stone-700">Sponsor booths · {booths.length}</span>
        {isOwner && (
          <Button
            onClick={() => setShowCreate(true)}
            size="sm"
            className="rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black h-7 text-[11px] ml-auto"
            data-testid="booth-create-btn"
          >
            <Plus size={11} className="mr-1"/> Add booth
          </Button>
        )}
      </div>
      {loading ? (
        <div className="p-6 text-center text-sm text-stone-500"><Loader2 className="inline animate-spin mr-1" size={13}/> Loading…</div>
      ) : booths.length === 0 ? (
        <div className="p-6 text-center text-sm text-stone-500">
          {isOwner ? "No booths yet — add one to run a giveaway wheel." : "Sponsor booths will appear here once organiser adds them."}
        </div>
      ) : (
        <ul className="divide-y divide-stone-100">
          {booths.map(b => (
            <li key={b.booth_id} className={`p-3 flex items-start gap-3 ${b.verified ? "bg-emerald-50/60" : ""}`} data-testid={`booth-${b.booth_id}`}>
              <div className={`w-11 h-11 rounded-2xl grid place-items-center shrink-0 ${b.verified ? "bg-emerald-100 text-emerald-700" : "bg-fuchsia-50 text-fuchsia-700"}`}>
                {b.verified ? <ShieldCheck size={18}/> : <Store size={18}/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-stone-900 truncate flex items-center gap-1.5">
                  {b.name}
                  {b.verified && (
                    <span className="inline-flex items-center gap-0.5 text-[9px] bg-emerald-600 text-white font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full" data-testid={`booth-verified-${b.booth_id}`}>
                      <ShieldCheck size={9}/> Verified
                    </span>
                  )}
                </div>
                {b.sponsor && <div className="text-[11px] font-black uppercase tracking-widest text-fuchsia-700">Sponsor · {b.sponsor}</div>}
                {b.description && <div className="text-xs text-stone-600 mt-0.5">{b.description}</div>}
                <div className="mt-1 flex flex-wrap gap-1">
                  {(b.prizes || []).map((p, i) => (
                    <span key={i} className="text-[10px] font-bold bg-amber-50 text-amber-800 rounded-full px-2 py-0.5" data-testid={`booth-prize-${b.booth_id}-${i}`}>
                      🎁 {p.name}{typeof p.stock === "number" ? ` · ${p.stock} left` : ""}
                    </span>
                  ))}
                </div>
                <div className="mt-1 text-[10px] text-stone-400 font-mono">{b.spin_count || 0} spins</div>
              </div>
              <div className="flex flex-col gap-1 items-end shrink-0">
                <Button
                  onClick={() => setSpinBooth(b)}
                  size="sm"
                  className="rounded-full bg-amber-500 hover:bg-amber-600 text-white font-black h-7 text-[11px]"
                  data-testid={`booth-spin-${b.booth_id}`}
                >
                  <Gift size={11} className="mr-1"/> Spin
                </Button>
                {isOwner && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => toggleVerified(b)}
                      className={`p-1 ${b.verified ? "text-emerald-700 hover:text-emerald-900" : "text-stone-400 hover:text-emerald-700"}`}
                      title={b.verified ? "Remove verified badge" : "Mark as verified sponsor"}
                      data-testid={`booth-verify-toggle-${b.booth_id}`}
                    >
                      <ShieldCheck size={13}/>
                    </button>
                    <button
                      onClick={() => setAnalytics(b)}
                      className="text-fuchsia-600 hover:text-fuchsia-800 p-1"
                      title="View booth analytics"
                      data-testid={`booth-analytics-${b.booth_id}`}
                    >
                      <BarChart3 size={13}/>
                    </button>
                    <a
                      href={posterUrl(b.booth_id, "A4")}
                      target="_blank" rel="noreferrer"
                      className="text-stone-600 hover:text-stone-900 p-1"
                      title="Download A4 QR poster"
                      data-testid={`booth-poster-${b.booth_id}`}
                    >
                      <Printer size={13}/>
                    </a>
                    <button
                      onClick={() => removeBooth(b)}
                      className="text-stone-400 hover:text-red-600 p-1"
                      title="Remove booth"
                      data-testid={`booth-del-${b.booth_id}`}
                    >
                      <Trash2 size={12}/>
                    </button>
                  </div>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
      {showCreate && <BoothCreateModal eventId={eventId} onClose={saved => { setShowCreate(false); if (saved) load(); }}/>}
      {spinBooth && <SpinWheelModal booth={spinBooth} eventId={eventId} onClose={() => { setSpinBooth(null); load(); }}/>}
      {analytics && <BoothAnalyticsModal eventId={eventId} booth={analytics} posterUrl={posterUrl} onClose={() => setAnalytics(null)}/>}
    </section>
  );
}

function BoothCreateModal({ eventId, onClose }) {
  const [name, setName] = useState("");
  const [sponsor, setSponsor] = useState("");
  const [description, setDescription] = useState("");
  const [prizesRaw, setPrizesRaw] = useState("T-Shirt · 20\nSticker pack · 60\nMacBook · 1");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error("Booth name required"); return; }
    const prizes = prizesRaw.split(/\n/).map(l => l.trim()).filter(Boolean).map(l => {
      const [n, w] = l.split(/\s*·\s*|\s*,\s*|\s*-\s*/);
      const weight = Math.max(1, Math.min(100, parseInt(w) || 1));
      return { name: (n || "").trim().slice(0, 80), weight };
    }).filter(p => p.name);
    if (prizes.length === 0) { toast.error("Add at least one prize"); return; }
    setBusy(true);
    try {
      await api.post(`/nfc/events/${eventId}/booths`, { name: name.trim(), sponsor: sponsor.trim() || null, description: description.trim() || null, prizes });
      toast.success("Booth added");
      onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={() => onClose(false)}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-5 w-full max-w-md space-y-3" data-testid="booth-create-modal">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-stone-900">New sponsor booth</h3>
          <button onClick={() => onClose(false)} className="text-stone-400 hover:text-stone-900"><X size={16}/></button>
        </div>
        <Input placeholder="Booth name (e.g. Emergent AI)" value={name} onChange={e => setName(e.target.value)} className="rounded-full text-sm" data-testid="booth-name"/>
        <Input placeholder="Sponsor (optional)" value={sponsor} onChange={e => setSponsor(e.target.value)} className="rounded-full text-sm" data-testid="booth-sponsor"/>
        <Textarea rows={2} placeholder="Description" value={description} onChange={e => setDescription(e.target.value)} className="rounded-2xl text-sm" data-testid="booth-desc"/>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Prizes (one per line · <em>name · weight</em>, higher weight = more likely)</div>
          <Textarea rows={4} value={prizesRaw} onChange={e => setPrizesRaw(e.target.value)} className="rounded-2xl text-sm font-mono" data-testid="booth-prizes"/>
        </div>
        <Button onClick={save} disabled={busy} className="w-full rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 font-black h-9" data-testid="booth-save">
          {busy ? <Loader2 size={13} className="animate-spin mr-1"/> : <Plus size={13} className="mr-1"/>} Add booth
        </Button>
      </div>
    </div>
  );
}

function SpinWheelModal({ booth, eventId, onClose }) {
  const [phase, setPhase] = useState("idle"); // idle | spinning | done
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const wheelPrizes = (booth.prizes || []).map(p => p.name);

  const spin = async () => {
    setPhase("spinning");
    setError(null);
    await new Promise(res => setTimeout(res, 1400));
    try {
      const r = await api.post(`/nfc/events/${eventId}/booths/${booth.booth_id}/spin`);
      if (r.data.reason === "sold_out") {
        setError("All prizes claimed 😢");
        setPhase("done");
        return;
      }
      setResult(r.data);
      setPhase("done");
    } catch (e) {
      setError(e?.response?.data?.detail || "Spin failed");
      setPhase("done");
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white p-6 w-full max-w-sm relative" data-testid="spin-wheel-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-stone-400 hover:text-stone-900"><X size={16}/></button>
        <h3 className="text-lg font-black text-stone-900 text-center">🎡 {booth.name}</h3>
        {booth.sponsor && <div className="text-[11px] text-fuchsia-700 font-black text-center uppercase tracking-widest">by {booth.sponsor}</div>}

        <div className="relative mx-auto my-5 w-56 h-56">
          <div
            className={`w-full h-full rounded-full shadow-2xl grid place-items-center overflow-hidden ${phase === "spinning" ? "animate-spin" : ""}`}
            style={{
              animationDuration: "0.6s",
              backgroundImage: "conic-gradient(from 0deg, #f59e0b, #ec4899, #10b981, #3b82f6, #f59e0b)",
            }}
          >
            <div className="w-40 h-40 rounded-full bg-white grid place-items-center text-center p-3">
              {phase === "idle" && <div className="text-sm font-black text-stone-700">Tap SPIN<br/>to win</div>}
              {phase === "spinning" && <Loader2 size={28} className="animate-spin text-fuchsia-600"/>}
              {phase === "done" && result?.prize && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-black text-emerald-700">You won</div>
                  <div className="text-base font-black text-stone-900 mt-1" data-testid="spin-result-prize">🎁 {result.prize}</div>
                  {result.already_spun && <div className="text-[10px] text-stone-500 mt-1">(You'd spun before — same prize)</div>}
                </div>
              )}
              {phase === "done" && error && <div className="text-sm font-bold text-rose-700" data-testid="spin-result-error">{error}</div>}
            </div>
          </div>
        </div>

        <div className="text-[10px] text-center text-stone-500">
          Prizes: {wheelPrizes.slice(0, 4).join(" · ")}{wheelPrizes.length > 4 && " …"}
        </div>

        <div className="mt-4 flex gap-2">
          {phase === "idle" && (
            <Button onClick={spin} className="flex-1 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white font-black h-10" data-testid="spin-start-btn">
              <Gift size={13} className="mr-1"/> Spin the wheel
            </Button>
          )}
          {phase === "done" && (
            <Button onClick={onClose} className="flex-1 rounded-full bg-stone-900 hover:bg-stone-800 text-white font-black h-10" data-testid="spin-close-btn">
              {result?.prize ? "Awesome, thanks!" : "Close"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function BoothAnalyticsModal({ eventId, booth, posterUrl, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/nfc/events/${eventId}/booths/${booth.booth_id}/analytics`);
        setData(r.data);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Couldn't load analytics");
      } finally { setLoading(false); }
    })();
  }, [eventId, booth.booth_id]);

  const max = Math.max(1, ...((data?.prizes || []).map(p => p.given)));

  return (
    <div className="fixed inset-0 bg-black/50 grid place-items-center p-4" style={{ zIndex: "var(--z-modal)" }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="rounded-3xl bg-white w-full max-w-lg p-5 relative max-h-[90vh] overflow-y-auto" data-testid="booth-analytics-modal">
        <button onClick={onClose} className="absolute top-3 right-3 text-stone-400 hover:text-stone-900"><X size={16}/></button>
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 size={16} className="text-fuchsia-700"/>
          <h3 className="text-lg font-black text-stone-900">{booth.name}</h3>
        </div>
        {booth.sponsor && <div className="text-[11px] uppercase tracking-widest font-black text-fuchsia-700 -mt-2 mb-3">Sponsor · {booth.sponsor}</div>}

        {loading ? (
          <div className="p-6 text-center text-sm text-stone-500"><Loader2 className="inline animate-spin mr-1" size={13}/> Loading analytics…</div>
        ) : !data ? (
          <div className="text-sm text-stone-500">No data yet.</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-2">
              <StatBox icon={Trophy}   label="Spins"          value={data.totals.spins}          color="amber"/>
              <StatBox icon={Users2}   label="Unique visitors" value={data.totals.unique_spinners} color="emerald"/>
              <StatBox icon={Sparkles} label="Prizes left"    value={data.totals.prizes_remaining >= 999900 ? "∞" : data.totals.prizes_remaining} color="fuchsia"/>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Prize heat-map</div>
              <div className="space-y-1">
                {data.prizes.map((p, i) => (
                  <div key={i} className="flex items-center gap-2" data-testid={`booth-analytics-prize-${i}`}>
                    <div className="w-32 text-xs font-bold text-stone-700 truncate">{p.name}</div>
                    <div className="flex-1 h-4 bg-stone-100 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-fuchsia-500 to-amber-500" style={{ width: `${(p.given / max) * 100}%` }}/>
                    </div>
                    <div className="text-[10px] font-mono text-stone-500 w-14 text-right">{p.given} · {p.hit_pct}%</div>
                  </div>
                ))}
              </div>
            </div>

            {data.spinners.length > 0 && (
              <div>
                <div className="text-[10px] uppercase tracking-widest font-black text-stone-500 mb-1">Who spun · lead follow-up list</div>
                <div className="border border-stone-200 rounded-2xl max-h-48 overflow-y-auto">
                  <table className="w-full text-[11px]">
                    <thead className="bg-stone-50 sticky top-0">
                      <tr>
                        <th className="text-left px-2 py-1 font-black text-stone-600">Name</th>
                        <th className="text-left px-2 py-1 font-black text-stone-600">Prize</th>
                        <th className="text-left px-2 py-1 font-black text-stone-600">Contact</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.spinners.map((s, i) => (
                        <tr key={s.user_id} className={i % 2 ? "bg-stone-50" : ""} data-testid={`booth-analytics-spinner-${i}`}>
                          <td className="px-2 py-1 font-bold text-stone-900">{s.user_name}</td>
                          <td className="px-2 py-1 text-stone-600">🎁 {s.prize}</td>
                          <td className="px-2 py-1 text-stone-500">
                            {s.email && <div>{s.email}</div>}
                            {s.mobile && <div>{s.mobile}</div>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <a href={posterUrl(booth.booth_id, "A4")} target="_blank" rel="noreferrer" className="flex-1 rounded-full bg-stone-900 hover:bg-stone-800 text-white text-center text-sm font-black py-2 inline-flex items-center justify-center gap-1" data-testid="booth-poster-a4-link">
                <Printer size={13}/> A4 poster
              </a>
              <a href={posterUrl(booth.booth_id, "A3")} target="_blank" rel="noreferrer" className="flex-1 rounded-full bg-stone-700 hover:bg-stone-800 text-white text-center text-sm font-black py-2 inline-flex items-center justify-center gap-1" data-testid="booth-poster-a3-link">
                <Printer size={13}/> A3 poster
              </a>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StatBox({ icon: Icon, label, value, color }) {
  const bg = { amber: "bg-amber-50 text-amber-800", emerald: "bg-emerald-50 text-emerald-800", fuchsia: "bg-fuchsia-50 text-fuchsia-800" }[color];
  return (
    <div className={`rounded-2xl ${bg} p-3 text-center`}>
      <Icon size={14} className="mx-auto mb-0.5 opacity-60"/>
      <div className="text-lg font-black">{value}</div>
      <div className="text-[10px] uppercase tracking-widest font-black opacity-70">{label}</div>
    </div>
  );
}
